import {
  FixedStakedObol__factory as FixedStakedObolFactory,
  ObolTestToken__factory as ObolTestTokenFactory,
  RebasingStakedObol__factory as RebasingStakedObolFactory,
} from "contracts/bindings"
import {
  getMainnetChainId,
  getSepoliaChainId,
} from "web3/helpers/chainReference"

enum Environment {
  Prod = "https://api.tally.xyz",
  Staging = "https://api.staging.tally.xyz",
}

export type LSTToken<Factory = any> = {
  address: string
  symbol: string
  name: string
  decimals: number
  chainId: string
  id: string
  factory: Factory
}

export type OrgToken = LSTToken<typeof ObolTestTokenFactory>

export type FixedLSTToken = LSTToken<typeof FixedStakedObolFactory>

export type RebasingLSTToken = LSTToken<typeof RebasingStakedObolFactory>

export type LSTData = {
  orgToken: OrgToken
  lstToken: FixedLSTToken
  rebasingLSTToken: RebasingLSTToken
  chainId: string
  uiConfig?: {
    tallyPoints?: boolean
  }
  useGetAliasedDelegateeFromRebasingContract?: boolean
}

// key is the org ID
const LST_CONSTANTS: Record<Environment, Record<string, LSTData>> = {
  [Environment.Staging]: {
    // OBOL LST TEST: https://staging.tally.xyz/gov/obol-test-st-gov
    "2580756208071739096": {
      chainId: getSepoliaChainId(),
      orgToken: {
        // https://sepolia.etherscan.io/address/0x830B162043b41908840aC8328Ce867CB6B5C2c74#readContract
        address: "0x830B162043b41908840aC8328Ce867CB6B5C2c74",
        symbol: "OBOLTEST2",
        name: "Obol Staker Test Token 2",
        decimals: 18,
        chainId: getSepoliaChainId(),
        id: `${getSepoliaChainId()}:0x830B162043b41908840aC8328Ce867CB6B5C2c74`,
        factory: ObolTestTokenFactory,
      },
      lstToken: {
        address: "0x2D0020Fc5BAA1c2fe02058c2E1eefA38e0e58519",
        symbol: "stOBOLTEST2",
        name: "Staked Obol Test",
        decimals: 18,
        chainId: getSepoliaChainId(),
        id: `${getSepoliaChainId()}:0x2D0020Fc5BAA1c2fe02058c2E1eefA38e0e58519`,
        factory: FixedStakedObolFactory,
      },
      rebasingLSTToken: {
        address: "0xDfdEB974D0A564d7C25610e568c1D309220236BB",
        symbol: "rstOBOLTEST2",
        name: "Rebasing Staked Obol Test",
        decimals: 18,
        chainId: getSepoliaChainId(),
        id: `${getSepoliaChainId()}:0xDfdEB974D0A564d7C25610e568c1D309220236BB`,
        factory: RebasingStakedObolFactory,
      },
      uiConfig: {
        tallyPoints: false,
      },
      useGetAliasedDelegateeFromRebasingContract: true,
    },
  },
  [Environment.Prod]: {
    "2413388957975839812": {
      chainId: getMainnetChainId(),
      orgToken: {
        // https://etherscan.io/address/0x0B010000b7624eb9B3DfBC279673C76E9D29D5F7#readContract
        address: "0x0B010000b7624eb9B3DfBC279673C76E9D29D5F7",
        symbol: "OBOL",
        name: "Obol Network",
        decimals: 18,
        chainId: getMainnetChainId(),
        id: `${getMainnetChainId()}:0x0B010000b7624eb9B3DfBC279673C76E9D29D5F7`,
        factory: ObolTestTokenFactory,
      },
      // https://etherscan.io/address/0x6590cBBCCbE6B83eF3774Ef1904D86A7B02c2fCC#readContract
      lstToken: {
        address: "0x6590cBBCCbE6B83eF3774Ef1904D86A7B02c2fCC",
        symbol: "stOBOL",
        name: "Staked Obol",
        decimals: 18,
        chainId: getMainnetChainId(),
        id: `${getMainnetChainId()}:0x6590cBBCCbE6B83eF3774Ef1904D86A7B02c2fCC`,
        factory: FixedStakedObolFactory,
      },
      // https://etherscan.io/address/0x1932e815254c53B3Ecd81CECf252A5AC7f0e8BeA#readContract
      rebasingLSTToken: {
        address: "0x1932e815254c53B3Ecd81CECf252A5AC7f0e8BeA",
        symbol: "rstOBOL",
        name: "Rebasing Staked Obol",
        decimals: 18,
        chainId: getMainnetChainId(),
        id: `${getMainnetChainId()}:0x1932e815254c53B3Ecd81CECf252A5AC7f0e8BeA`,
        factory: RebasingStakedObolFactory,
      },
      uiConfig: {
        tallyPoints: false,
      },
    },
  },
}
function getNullishData(orgId?: string) {
  if (!orgId) {
    return null
  }

  const env = process.env.NEXT_PUBLIC_TALLY_API_URL as Environment

  return LST_CONSTANTS[env]?.[orgId] ?? null
}

export function hasLSTStakerData(orgId?: string) {
  return Boolean(getNullishData(orgId))
}

export function getLSTStakerConstants(orgId?: string) {
  const data = getNullishData(orgId)

  if (!data) {
    throw Error(`No LSTStaker config for orgId "${orgId}"`)
  }

  return data
}
